import Store from '../wrappers/Store';
import Settings from '../wrappers/Settings';
import { sqliteStorage } from './SqliteStorage';
import { diContainer } from '../DIContainer';
import { get, set, del } from 'idb-keyval';
const defaultPrefixFactory = () => {
    return Store.get('tenant')?.venue?.hash || '';
};
class AppStorage {
    static instance = null;
    isMobile = false;
    isElectron = false;
    prefixFactory = defaultPrefixFactory;
    static getInstance() {
        if (!AppStorage.instance) {
            AppStorage.instance = new AppStorage();
        }
        return AppStorage.instance;
    }
    async init(device) {
        this.isMobile = device.platform === 'android' || device.platform === 'ios';
        this.isElectron = device.platform === 'electron';
        if (this.isMobile) {
            await sqliteStorage.initialize();
        }
    }
    setPrefixFactory(factory) {
        this.prefixFactory = factory;
    }
    getPrefixedKey(key, prefix) {
        if (prefix === undefined) {
            prefix = this.prefixFactory();
        }
        if (prefix) {
            key = prefix + '-' + key;
        }
        return key;
    }
    parseJSON(str) {
        if (!str)
            return null;
        try {
            return JSON.parse(str);
        }
        catch (e) {
            const errorReporter = diContainer.resolve('errorReporter');
            errorReporter.reportMessage(`AppStorage error parsing JSON: ${e.message}`);
            return null;
        }
    }
    stringifyJSON(value) {
        try {
            const strValue = JSON.stringify(value);
            if (strValue === undefined)
                return 'null';
            return strValue;
        }
        catch (e) {
            const errorReporter = diContainer.resolve('errorReporter');
            errorReporter.reportMessage(`AppStorage error stringifying JSON: ${e.message}`);
            throw e;
        }
    }
    handleStorageError(operation, error) {
        // Capacitor/native-bridge rejections are often plain {message, code}
        // objects, not Error instances — pull the message so the report says
        // more than "[object Object]".
        const detail = error instanceof Error ? error.message
            : (error !== null && typeof error === 'object' && 'message' in error) ? String(error.message)
                : String(error);
        const errorMessage = error instanceof Error && error.name === 'QuotaExceededError'
            ? 'Storage quota exceeded'
            : `Storage operation failed: ${detail}`;
        const errorReporter = diContainer.resolve('errorReporter');
        errorReporter.reportMessage(`AppStorage ${operation}: ${errorMessage}`);
        console.error(`AppStorage ${operation}: ${errorMessage}`);
    }
    local() {
        return {
            getItem: async (key, prefix) => {
                const prefixedKey = this.getPrefixedKey(key, prefix);
                try {
                    if (this.isMobile) {
                        const value = await sqliteStorage.getItem(prefixedKey);
                        return this.parseJSON(value);
                    }
                    return this.parseJSON(localStorage.getItem(prefixedKey));
                }
                catch (error) {
                    this.handleStorageError('local.getItem', error);
                    return null;
                }
            },
            setItem: async (key, value, prefix) => {
                const prefixedKey = this.getPrefixedKey(key, prefix);
                const stringValue = this.stringifyJSON(value);
                try {
                    if (this.isMobile) {
                        await sqliteStorage.setItem(prefixedKey, stringValue);
                    }
                    else {
                        localStorage.setItem(prefixedKey, stringValue);
                    }
                }
                catch (error) {
                    this.handleStorageError('local.setItem', error);
                }
            },
            deleteItem: async (key, prefix) => {
                const prefixedKey = this.getPrefixedKey(key, prefix);
                try {
                    if (this.isMobile) {
                        await sqliteStorage.deleteItem(prefixedKey);
                    }
                    else {
                        localStorage.removeItem(prefixedKey);
                    }
                }
                catch (error) {
                    this.handleStorageError('local.deleteItem', error);
                }
            }
        };
    }
    store() {
        return {
            getItem: async (key) => {
                try {
                    if (this.isMobile) {
                        const value = await sqliteStorage.getItem(key);
                        return this.parseJSON(value);
                    }
                    const value = await get(key);
                    return this.parseJSON(value);
                }
                catch (error) {
                    this.handleStorageError('store.getItem', error);
                    return null;
                }
            },
            setItem: async (key, value) => {
                const stringValue = this.stringifyJSON(value);
                try {
                    if (this.isMobile) {
                        await sqliteStorage.setItem(key, stringValue);
                    }
                    else {
                        await set(key, stringValue);
                    }
                }
                catch (error) {
                    this.handleStorageError('store.setItem', error);
                }
            },
            deleteItem: async (key) => {
                try {
                    if (this.isMobile) {
                        await sqliteStorage.deleteItem(key);
                    }
                    else {
                        await del(key);
                    }
                }
                catch (error) {
                    this.handleStorageError('store.deleteItem', error);
                }
            }
        };
    }
    native() {
        return {
            getItem: async (key, prefix) => {
                const prefixedKey = this.getPrefixedKey(key, prefix);
                try {
                    if (this.isElectron) {
                        const value = await Settings.electron.store?.get(prefixedKey);
                        return this.parseJSON(value);
                    }
                    if (this.isMobile) {
                        const value = await sqliteStorage.getItem(prefixedKey);
                        return this.parseJSON(value);
                    }
                    return this.parseJSON(localStorage.getItem(prefixedKey));
                }
                catch (error) {
                    this.handleStorageError('native.getItem', error);
                    return null;
                }
            },
            setItem: async (key, value, prefix) => {
                const prefixedKey = this.getPrefixedKey(key, prefix);
                const stringValue = this.stringifyJSON(value);
                try {
                    if (this.isElectron) {
                        await Settings.electron.store?.set(prefixedKey, stringValue);
                    }
                    else if (this.isMobile) {
                        await sqliteStorage.setItem(prefixedKey, stringValue);
                    }
                    else {
                        localStorage.setItem(prefixedKey, stringValue);
                    }
                }
                catch (error) {
                    this.handleStorageError('native.setItem', error);
                }
            },
            deleteItem: async (key, prefix) => {
                const prefixedKey = this.getPrefixedKey(key, prefix);
                try {
                    if (this.isElectron) {
                        await Settings.electron.store?.delete(prefixedKey);
                    }
                    else if (this.isMobile) {
                        await sqliteStorage.deleteItem(prefixedKey);
                    }
                    else {
                        localStorage.removeItem(prefixedKey);
                    }
                }
                catch (error) {
                    this.handleStorageError('native.deleteItem', error);
                }
            }
        };
    }
}
export const appStorage = AppStorage.getInstance();
//# sourceMappingURL=AppStorage.js.map